import { GENERATED_ROLE_DEFAULTS_VERSION } from '@revelio/data-access/role-defaults';
import { FormatType } from '@revelio/iso-utility';

import {
  PageType,
  type ReportPageConfig,
} from '../../components/PageConfigProvider/types';
import {
  PlotFilterType,
  type TableColumnConfig,
} from '../../components/PageConfigProvider/types/item';
import type { MetricValues } from '../../components/PageConfigProvider/types/metric';
import { FilterId } from '../../filters/types/filters';
import {
  buildFilterGroups,
  encodeFilterGroups,
} from '../../filters/utils/filterGroupsUtils';
import { buildReportPages } from './report-page-builder';

const targetGeoParam = { parameterId: 'target-geography', grouped: false };
const comparisonGeoUngrouped = {
  parameterId: 'comparison-geography',
  grouped: false,
};
const comparisonGeoGrouped = {
  parameterId: 'comparison-geography',
  grouped: true,
};
const occupationParam = { parameterId: 'occupation', grouped: false };

const standardPlotParameters = [
  targetGeoParam,
  comparisonGeoUngrouped,
  occupationParam,
];

const standardPlotParametersGrouped = [
  targetGeoParam,
  comparisonGeoGrouped,
  occupationParam,
];

const snowflakeRoleSubFields = [
  {
    name: 'role_k50_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['role_k50'],
  },
  {
    name: 'role_k150_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['role_k150'],
  },
  {
    name: 'role_k500_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['role_k500'],
  },
  {
    name: 'role_k1500_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['role_k1500'],
  },
];

const snowflakeSkillSubFields = [
  {
    name: 'skill_k150_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['skill_k150'],
  },
  {
    name: 'skill_k1500_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['skill_k1500'],
  },
  {
    name: 'skill_k5000_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['skill_k5000'],
  },
];

const snowflakeTargetGeoSubFields = [
  {
    name: 'target_geo_level',
    fieldType: 'string' as const,
    selectionLevelIds: [] as string[],
    extractMode: 'levelName' as const,
  },
  {
    name: 'target_geo_id',
    fieldType: 'number' as const,
    selectionLevelIds: [] as string[],
  },
];

const snowflakeComparisonGeoSubFields = [
  {
    name: 'comparison_msa_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['msa'],
  },
  {
    name: 'comparison_state_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['state'],
  },
  {
    name: 'comparison_country_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['country'],
  },
  {
    name: 'comparison_region_ids',
    fieldType: 'number[]' as const,
    selectionLevelIds: ['region'],
  },
];

const snowflakeLandscapeFields = [
  {
    name: 'role',
    label: 'Role',
    value: '',
    required: false,
    fieldType: 'number[]' as const,
    parameterId: 'occupation',
    subFields: snowflakeRoleSubFields,
  },
  {
    name: 'skill',
    label: 'Skill',
    value: '',
    required: false,
    fieldType: 'number[]' as const,
    parameterId: 'skills',
    subFields: snowflakeSkillSubFields,
  },
  {
    name: 'target_geography',
    label: 'Target Geography',
    value: '',
    required: true,
    fieldType: 'string' as const,
    parameterId: 'target-geography',
    subFields: snowflakeTargetGeoSubFields,
  },
  {
    name: 'seniority_levels',
    label: 'Seniority',
    value: '',
    required: false,
    fieldType: 'number[]' as const,
    parameterId: 'seniority',
  },
];

const competitiveLandscapeMetrics: MetricValues[] = [
  'screener_headcount',
  'market_share',
  'screener_salary',
  'postings_active',
  'overall',
];

export const reportMarketConditions: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages(
    'market-conditions',
    [
      [
        {
          id: 'ai-summary',
          title: '',
          position: { y: -1, height: 33 },
          plot: {
            chartType: 'ai-summary',
            textHtml: '## Executive Summary',
            plotParameters: [
              targetGeoParam,
              comparisonGeoUngrouped,
              occupationParam,
            ],
          },
        },
      ],
      [
        {
          id: 'supply-text',
          title: '',
          position: { height: 7 },
          plot: {
            chartType: 'text',
            textHtml:
              "## Labor supply & demand\n\nThe availability of workers within a geographic area forms the foundation of labor supply. Factors such as population demographics, educational attainment, and workforce participation rates contribute to the size and composition of the available talent pool. It's important to note that changes in labor supply occur gradually, influenced by long-term trends in population growth and educational investments.",
          },
        },
        {
          id: 'supply-bar',
          title:
            'Supply{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { y: 7, width: 24, height: 11 },
          plot: {
            chartType: 'bar',
            sort: 'DESC',
            dimensionSource: 'primary',
            defaultMetricOptions: ['headcount'],
            plotParameters: standardPlotParameters,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'demand-text',
          title: '',
          position: { y: 18, height: 6 },
          plot: {
            chartType: 'text',
            textHtml:
              "The demand for talent, as reflected in job postings, underscores the competitive nature of today's job market. Unlike the gradual evolution of labor supply, demand can fluctuate rapidly in response to changes in the business environment. These fluctuations may stem from shifts in consumer preferences, advancements in technology, changes in regulations, or sudden shifts in market conditions.",
          },
        },
        {
          id: 'demand-bar',
          title:
            'Demand{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { x: 24, y: 7, width: 24, height: 11 },
          plot: {
            chartType: 'bar',
            sort: 'DESC',
            dimensionSource: 'geography',
            defaultMetricOptions: ['postings_active'],
            plotParameters: standardPlotParameters,
          },
          isTitleManuallySet: true,
        },
      ],
      [
        {
          id: 'ratio-text',
          title: '',
          position: { height: 8 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Demand-to-supply ratio\n\nBringing the supply and demand together allows us to assess the competitive landscape for available talent. A higher ratio of job postings per employee indicates a challenging hiring environment, where there are more open positions relative to available candidates, leading to increased competition for talent. Conversely, a lower ratio signals a more favorable hiring environment, as employers have fewer positions to fill and a wider pool of candidates to choose from, reducing the competition for talent.',
          },
        },
        {
          id: 'ratio-line',
          title:
            'Demand to supply ratio{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { x: 8, y: 8, width: 32, height: 8 },
          plot: {
            chartType: 'line',
            plotParameters: standardPlotParameters,
            defaultMetricOptions: ['demand_supply'],
            curveSmoothness: 0.9,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'ratio-dynamic-text',
          title: '',
          position: { y: 16, width: 25, height: 8 },
          plot: {
            chartType: 'text',
            plotParameters: standardPlotParameters,
            defaultMetricOptions: ['demand_supply_percentile'],
            textHtml: `{{#if (HAS_DATA "demand_supply_percentile")}}
The market{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}} in {{PARAM "Target Geography"}} ranks at the {{FORMAT (multiply (VALUE "demand_supply_percentile") 100) format="ORDINAL"}} percentile across all labor markets for labor demand relative to supply.
{{#if (lte (VALUE "demand_supply_percentile") 0.2)}}
The low ratio of job postings to employees indicates a relaxed hiring environment, with ample availability of talent for recruiting{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}}.
{{else if (lte (VALUE "demand_supply_percentile") 0.4)}}
The relatively low ratio of job postings to employees suggests a favorable hiring environment, with manageable competition recruiting{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}}.
{{else if (lte (VALUE "demand_supply_percentile") 0.6)}}
The balanced ratio of job postings to employees indicates moderate competition{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}}.
{{else if (lte (VALUE "demand_supply_percentile") 0.8)}}
The higher ratio of job postings to employees signals increasing competition for talent, making it more challenging to recruit{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}}.
{{else}}
The very high ratio of job postings to employees indicates intense competition among employers, creating a challenging hiring environment{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}}.
{{/if}}
{{else}}
There's currently no available data to assess the talent ratio in {{PARAM "Target Geography"}}.
{{/if}}`,
          },
        },
        {
          id: 'ratio-gauge',
          title: 'Demand to supply ratio rank',
          isTitleManuallySet: true,
          position: { x: 26, y: 16, width: 22, height: 8 },
          plot: {
            chartType: 'gauge-v2',
            defaultMetricOptions: ['demand_supply_percentile'],
            plotParameters: standardPlotParameters,
            gaugeV2: {
              value: '{{VALUE "demand_supply_percentile"}}',
              minLabel: 'less talent',
              maxLabel: 'more talent',
            },
            colorPalette: ['#3DC679', '#FF646D'],
          },
        },
      ],
      [
        {
          id: 'salary-text',
          title: '',
          position: { height: 8 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Salary\n\nSalary plays an integral role when making location decisions. While it serves as an indicator of the quality of talent, variances in the cost of living among different locations can significantly impact the expenses associated with hiring equally skilled employees. Therefore, it is ideal to find a location with a high quality of talent in a strategic balance with reasonable living costs. This ensures not only the acquisition of top-notch talent but also an efficient allocation of resources.',
          },
        },
        {
          id: 'salary-bar',
          title:
            'Salary{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { y: 8, width: 23, height: 9 },
          plot: {
            chartType: 'bar',
            sort: 'DESC',
            dimensionSource: 'geography',
            defaultMetricOptions: ['base_salary'],
            plotParameters: standardPlotParameters,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'salary-growth-line',
          title:
            'Salary change{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}} (YoY)',
          position: { x: 23, y: 8, width: 25, height: 9 },
          plot: {
            chartType: 'line',
            defaultMetricOptions: ['wage_pressure'],
            plotParameters: standardPlotParameters,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'salary-growth-text',
          title: '',
          position: { y: 17, height: 4 },
          plot: {
            chartType: 'text',
            textHtml:
              'Examining the trajectory of salary growth provides valuable insights into future expectations. By analyzing historical trends, we gain a clearer understanding of how salaries have evolved over time and can anticipate potential patterns for future growth.',
          },
        },
        {
          id: 'salary-dynamic-text',
          title: '',
          position: { y: 21, width: 26, height: 8 },
          plot: {
            chartType: 'text',
            plotParameters: standardPlotParameters,
            defaultMetricOptions: ['wage_pressure_percentile'],
            textHtml: `{{#if (HAS_DATA "wage_pressure_percentile")}}
The market{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}} in {{PARAM "Target Geography"}} ranks at the {{FORMAT (multiply (VALUE "wage_pressure_percentile") 100) format="ORDINAL"}} percentile across all labor markets for salary growth.
{{#if (lte (VALUE "wage_pressure_percentile") 0.2)}}
This indicates a relatively low level of salary growth compared to the comparison geography.
{{else if (lte (VALUE "wage_pressure_percentile") 0.4)}}
It suggests a moderate level of salary growth compared to the comparison geography, offering favorable conditions for hiring.
{{else if (lte (VALUE "wage_pressure_percentile") 0.6)}}
This implies a balanced level of salary growth compared to the comparison geography, providing stable conditions for hiring.
{{else if (lte (VALUE "wage_pressure_percentile") 0.8)}}
It reflects a relatively high level of salary growth compared to the comparison geography, presenting challenges for hiring.
{{else}}
It has experienced a significant level of salary growth compared to the comparison geography, posing difficulties for hiring.
{{/if}}
{{else}}
There's currently no available data to assess the wage pressure in {{PARAM "Target Geography"}}.
{{/if}}`,
          },
        },
        {
          id: 'salary-gauge',
          title: 'Salary change rank',
          isTitleManuallySet: true,
          position: { x: 26, y: 21, width: 22, height: 8 },
          plot: {
            chartType: 'gauge-v2',
            defaultMetricOptions: ['wage_pressure_percentile'],
            plotParameters: standardPlotParameters,
            gaugeV2: {
              value: '{{VALUE "wage_pressure_percentile"}}',
              minLabel: 'slower',
              maxLabel: 'faster',
            },
            colorPalette: ['#3DC679', '#FF646D'],
          },
        },
      ],
      [
        {
          id: 'time-to-fill-text',
          title: '',
          position: { height: 6 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Time to fill for job postings\n\nA shorter time to fill for job postings not only signifies a more accessible talent pool but also enables firms to be flexible and responsive to changing market demands. Conversely, a prolonged time to fill suggests a more challenging hiring environment, potentially hindering business agility.',
          },
        },
        {
          id: 'time-to-fill-bar',
          title:
            'Time to Fill{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}} job postings in {{PARAM "Target Geography"}}',
          position: { x: 10, y: 6, width: 31, height: 10 },
          plot: {
            chartType: 'bar',
            sort: 'DESC',
            defaultMetricOptions: ['time_to_fill'],
            plotParameters: standardPlotParameters,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'time-to-fill-dynamic',
          title: '',
          position: { y: 16, width: 26, height: 8 },
          plot: {
            chartType: 'text',
            plotParameters: standardPlotParameters,
            defaultMetricOptions: ['time_to_fill_percentile'],
            textHtml: `{{#if (HAS_DATA "time_to_fill_percentile")}}
The market{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}} in {{PARAM "Target Geography"}} ranks at the {{FORMAT (multiply (VALUE "time_to_fill_percentile") 100) format="ORDINAL"}} percentile across all labor markets for time to fill vacancies.
{{#if (lte (VALUE "time_to_fill_percentile") 0.2)}}
This suggests a relatively short duration for filling job openings compared to the comparison geography. For you, this means quicker access to talent and potentially lower recruitment costs.
{{else if (lte (VALUE "time_to_fill_percentile") 0.4)}}
It indicates a moderate duration for filling job openings compared to the comparison geography. You may need to balance recruitment efforts to ensure timely hiring while maintaining quality.
{{else if (lte (VALUE "time_to_fill_percentile") 0.6)}}
This implies a balanced duration for filling job openings compared to the comparison geography. You can expect reasonable timeframes for hiring, allowing for efficient workforce planning.
{{else if (lte (VALUE "time_to_fill_percentile") 0.8)}}
It reflects a relatively lengthy duration for filling job openings compared to the comparison geography. You may face challenges in recruiting and retaining talent, requiring proactive hiring strategies.
{{else}}
It indicates a significantly lengthy duration for filling job openings compared to the comparison geography. You may experience delays in hiring, impacting productivity and growth objectives.
{{/if}}
{{else}}
There's currently no available data to assess time to fill for vacancies in {{PARAM "Target Geography"}}.
{{/if}}`,
          },
        },
        {
          id: 'time-to-fill-gauge',
          title: 'Time to fill rank',
          isTitleManuallySet: true,
          position: { x: 26, y: 16, width: 22, height: 8 },
          plot: {
            chartType: 'gauge-v2',
            defaultMetricOptions: ['time_to_fill_percentile'],
            plotParameters: standardPlotParameters,
            gaugeV2: {
              value: '{{VALUE "time_to_fill_percentile"}}',
              minLabel: 'shorter',
              maxLabel: 'longer',
            },
            colorPalette: ['#3DC679', '#FF646D'],
          },
        },
      ],
      [
        {
          id: 'market-tightness-text',
          title: '',
          position: { height: 6 },
          plot: {
            chartType: 'text',
            textHtml:
              "## Market tightness\n\nOur market tightness score integrates crucial elements such as the supply-to-labor ratio, time-to-fill, and salary pressure into a single metric. This consolidation offers businesses a comprehensive understanding of the prevailing hiring conditions in today's job market, aiding in informed decision-making and strategic workforce planning.",
          },
        },
        {
          id: 'market-tightness-bar',
          title:
            'Market tightness{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { x: 14, y: 6, width: 23, height: 9 },
          plot: {
            chartType: 'bar',
            sort: 'DESC',
            dimensionSource: 'geography',
            defaultMetricOptions: ['market_tightness'],
            plotParameters: standardPlotParameters,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'market-tightness-dynamic',
          title: '',
          position: { y: 15, width: 26, height: 8 },
          plot: {
            chartType: 'text',
            plotParameters: standardPlotParameters,
            defaultMetricOptions: ['market_tightness'],
            textHtml: `{{#if (HAS_DATA "market_tightness")}}
{{#if (lte (VALUE "market_tightness") 0.2)}}
{{PARAM "Target Geography"}} offers an extremely loose job market. Employers have a significant advantage in this market and can afford to be highly selective in their hiring decisions. As an employer, you might consider offering significantly below the prevailing market rate.
{{else if (lte (VALUE "market_tightness") 0.4)}}
{{PARAM "Target Geography"}} offers a moderately loose job market. Employers have an advantage in this market and can afford to be somewhat selective in their hiring decisions. As an employer, you might consider offering slightly below the prevailing market rate.
{{else if (lte (VALUE "market_tightness") 0.6)}}
{{PARAM "Target Geography"}} offers a balanced job market. Neither employers nor employees have any particular advantage in this market. As an employer, you might consider offering the prevailing market rate to attract the desired talent.
{{else if (lte (VALUE "market_tightness") 0.8)}}
{{PARAM "Target Geography"}} offers a moderately tight job market. Employers face some competition for qualified candidates in this market. As an employer, you might consider offering slightly above the prevailing market rate to attract the desired talent.
{{else}}
{{PARAM "Target Geography"}} offers an extremely tight job market. Employers face intense competition for qualified candidates in this market. As an employer, you would be at a disadvantage and might need to offer significantly above the prevailing market rate to attract the desired talent.
{{/if}}
{{else}}
There's currently no available data to assess the market tightness in {{PARAM "Target Geography"}}.
{{/if}}`,
          },
        },
        {
          id: 'market-tightness-gauge',
          title: 'Market tightness',
          isTitleManuallySet: true,
          position: { x: 26, y: 15, width: 22, height: 8 },
          plot: {
            chartType: 'gauge-v2',
            defaultMetricOptions: ['market_tightness'],
            plotParameters: standardPlotParameters,
            gaugeV2: {
              value: '{{VALUE "market_tightness"}}',
              minLabel: 'less competitive',
              maxLabel: 'more competitive',
            },
            colorPalette: ['#3DC679', '#FF646D'],
          },
        },
      ],
      [
        {
          id: 'skills-intro-text',
          title: '',
          position: { height: 7 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Skills\n\nOur data goes beyond mere job titles, delving into the specific skills that employees bring to the table. This comprehensive understanding of the workforce enables us to precisely identify the expertise required to meet the hiring needs.',
          },
        },
        {
          id: 'd45e2ea1-bedd-479f-8668-d7c9dc3b3321',
          title:
            'Skill profile{{#if (HAS_PARAM "Occupation")}} of {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          isTitleManuallySet: true,
          isTitleCentered: true,
          position: { y: 7, height: 22 },
          plot: {
            chartType: 'diverging-bar-combo',
            secondaryDimension: 'gender',
            defaultMetricOptions: ['headcount'],
            showDataLabels: true,
            enableRowSelection: false,
            format: FormatType.PERCENTAGE,
            dataSource: 'snowflake',
            plotParameters: [targetGeoParam, comparisonGeoGrouped],
            comparisonTransformation: {
              baseline: 'second',
            },
            snowflakeConfig: {
              savedQueryId: '9897262d-84e8-457a-8fcf-55e4abb5f2d3',
              fields: [
                {
                  name: 'role',
                  label: 'Role',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'occupation',
                  subFields: snowflakeRoleSubFields,
                },
                {
                  name: 'skill',
                  label: 'Skill',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'skills',
                  subFields: snowflakeSkillSubFields,
                },
                {
                  name: 'target_geography',
                  label: 'Target Geography',
                  value: '',
                  required: true,
                  fieldType: 'string',
                  parameterId: 'target-geography',
                  subFields: snowflakeTargetGeoSubFields,
                },
                {
                  name: 'comparison_geography',
                  label: 'Comparison Geography',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'comparison-geography',
                  subFields: snowflakeComparisonGeoSubFields,
                },
                {
                  name: 'custom_tax_id',
                  label: 'Custom Taxonomy ID',
                  value: '',
                  required: false,
                  fieldType: 'string',
                },
                {
                  name: 'seniority_levels',
                  label: 'Seniority',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'seniority',
                },
              ],
            },
          },
        },
      ],
      [
        {
          id: 'demographics-text',
          title: '',
          position: { width: 22, height: 9 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Demographics\n\nDifferent locations offer distinct opportunities for cultivating diverse teams and tapping into unique perspectives. Recognizing and leveraging these regional differences enriches organizational culture, fostering adaptability in an ever-evolving landscape.',
          },
        },
        {
          id: 'demographics-gender-bar',
          title:
            'Gender distribution{{#if (HAS_PARAM "Occupation")}} of {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { x: 22, y: 2, width: 26, height: 7 },
          plot: {
            chartType: 'stacked-bar',
            secondaryDimension: 'gender',
            defaultMetricOptions: ['headcount'],
            plotParameters: standardPlotParametersGrouped,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'demographics-ethnicity-bar',
          title:
            'Ethnicity distribution{{#if (HAS_PARAM "Occupation")}} of {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { y: 9, width: 27, height: 7 },
          plot: {
            chartType: 'stacked-bar',
            secondaryDimension: 'ethnicity',
            defaultMetricOptions: ['headcount'],
            plotParameters: standardPlotParametersGrouped,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'demographics-dynamic',
          title: '',
          position: { x: 27, y: 9, width: 21, height: 7 },
          plot: {
            chartType: 'text',
            plotParameters: standardPlotParametersGrouped,
            defaultMetricOptions: ['headcount'],
            textHtml: `{{#if (HAS_DATA "headcount" secondaryDimension="gender")}}{{#if (HAS_DATA "headcount" secondaryDimension="ethnicity")}}
{{#if (VALUE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")}}
{{#if (gt (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) 10)}}
{{#if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 10)}}{{PARAM "Target Geography"}} offers significantly higher female representation and significantly higher ethnic diversity than comparison geography.
{{else if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 5)}}{{PARAM "Target Geography"}} offers significantly higher female representation but higher ethnic diversity than comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -5)}}{{PARAM "Target Geography"}} offers significantly higher female representation and similar ethnic diversity to comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -10)}}{{PARAM "Target Geography"}} offers significantly higher female representation but lower ethnic diversity than comparison geography.
{{else}}{{PARAM "Target Geography"}} offers significantly higher female representation but significantly lower ethnic diversity than comparison geography.
{{/if}}
{{else if (gt (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) 5)}}
{{#if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 10)}}{{PARAM "Target Geography"}} offers higher female representation but significantly higher ethnic diversity than comparison geography.
{{else if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 5)}}{{PARAM "Target Geography"}} offers higher female representation and ethnic diversity than comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -5)}}{{PARAM "Target Geography"}} offers higher female representation and similar ethnic diversity than comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -10)}}{{PARAM "Target Geography"}} offers higher female representation but lower ethnic diversity than comparison geography.
{{else}}{{PARAM "Target Geography"}} offers higher female representation but significantly lower ethnic diversity than comparison geography.
{{/if}}
{{else if (gte (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) -5)}}
{{#if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 10)}}{{PARAM "Target Geography"}} offers similar female representation but significantly higher ethnic diversity than comparison geography.
{{else if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 5)}}{{PARAM "Target Geography"}} offers similar female representation but higher ethnic diversity than comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -5)}}{{PARAM "Target Geography"}} offers similar female representation and ethnic diversity to comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -10)}}{{PARAM "Target Geography"}} offers similar female representation but lower ethnic diversity than comparison geography.
{{else}}{{PARAM "Target Geography"}} offers similar female representation but significantly lower ethnic diversity than comparison geography.
{{/if}}
{{else if (gte (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) -10)}}
{{#if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 10)}}{{PARAM "Target Geography"}} offers lower female representation but significantly higher ethnic diversity than comparison geography.
{{else if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 5)}}{{PARAM "Target Geography"}} offers lower female representation but higher ethnic diversity than comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -5)}}{{PARAM "Target Geography"}} offers lower female representation and similar ethnic diversity to comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -10)}}{{PARAM "Target Geography"}} offers lower female representation and ethnic diversity than comparison geography.
{{else}}{{PARAM "Target Geography"}} offers lower female representation but significantly lower ethnic diversity than comparison geography.
{{/if}}
{{else}}
{{#if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 10)}}{{PARAM "Target Geography"}} offers significantly lower female representation but significantly higher ethnic diversity than comparison geography.
{{else if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 5)}}{{PARAM "Target Geography"}} offers significantly lower female representation but higher ethnic diversity than comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -5)}}{{PARAM "Target Geography"}} offers significantly lower female representation and similar ethnic diversity to comparison geography.
{{else if (gte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) -10)}}{{PARAM "Target Geography"}} offers significantly lower female representation but lower ethnic diversity than comparison geography.
{{else}}{{PARAM "Target Geography"}} offers significantly lower female representation and ethnic diversity than comparison geography.
{{/if}}
{{/if}}
{{else}}
{{PARAM "Target Geography"}} has female representation of {{FORMAT (MATH "female_share * 100" female_share=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography")) format=".0f"}}%.
{{/if}}
{{/if}}
{{else}}
There is no available data to assess the demographics of {{PARAM "Target Geography"}}.
{{/if}}`,
          },
        },
        {
          id: 'experience-dynamic',
          title: '',
          position: { y: 16, width: 21, height: 6 },
          plot: {
            chartType: 'text',
            plotParameters: standardPlotParametersGrouped,
            defaultMetricOptions: ['tenure'],
            textHtml: `{{#if (HAS_DATA "tenure")}}
The average relevant experience of workers{{#if (HAS_PARAM "Occupation")}} in {{PARAM "Occupation"}}{{/if}} in {{PARAM "Target Geography"}} is around {{FORMAT (VALUE "tenure" entity=(PARAM "Target Geography")) format=".0f"}} years. This is {{#if (gte (subtract (VALUE "tenure" entity=(PARAM "Target Geography")) (VALUE "tenure" entity="Comparison Geography")) 0)}}{{FORMAT (MATH "(target - comparison) / comparison * 100" target=(VALUE "tenure" entity=(PARAM "Target Geography")) comparison=(VALUE "tenure" entity="Comparison Geography")) format=".0f"}}% higher{{else}}{{FORMAT (MATH "(comparison - target) / comparison * 100" target=(VALUE "tenure" entity=(PARAM "Target Geography")) comparison=(VALUE "tenure" entity="Comparison Geography")) format=".0f"}}% lower{{/if}} than the comparison geography.
{{else}}
There is no available data to assess the average relevant experience of workers{{#if (HAS_PARAM "Occupation")}} in {{PARAM "Occupation"}}{{/if}} in this market.
{{/if}}`,
          },
          isTitleManuallySet: true,
        },
        {
          id: 'experience-bar',
          title:
            'Years of relevant experience{{#if (HAS_PARAM "Occupation")}} of {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { x: 21, y: 16, width: 27, height: 7 },
          plot: {
            chartType: 'bar',
            sort: 'DESC',
            defaultMetricOptions: ['tenure'],
            plotParameters: standardPlotParametersGrouped,
          },
          isTitleManuallySet: true,
        },
      ],
      [
        {
          id: 'talent-pipeline-text',
          title: '',
          position: { width: 26, height: 13 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Talent pipeline\n\nAnalyzing the educational backgrounds of employees may help streamline recruitment efforts by highlighting schools with strong talent pipelines. The prestige and reputation of the institutions from which individuals graduate can serve as a strong indicator of talent, particularly for early-career professionals.',
          },
        },
        {
          id: '2f129239-a11f-4af5-8ea0-c4ce20d5d158',
          title:
            'Prestige distribution{{#if (HAS_PARAM "Occupation")}} of {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          isTitleManuallySet: false,
          position: { x: 26, width: 22, height: 14 },
          plot: {
            chartType: 'grouped-bar',
            defaultMetricOptions: ['headcount'],
            enableRowSelection: false,
            format: FormatType.PERCENTAGE,
            dataSource: 'snowflake',
            plotParameters: [targetGeoParam],
            colorPalette: ['#c1c7d1', '#5ca9e9'],
            showDataLabels: true,
            orientation: 'vertical',
            snowflakeConfig: {
              savedQueryId: 'geography-prestige-distribution',
              fields: [
                {
                  name: 'role',
                  label: 'role',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'occupation',
                  subFields: snowflakeRoleSubFields,
                },
                {
                  name: 'skill',
                  label: 'skill',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  subFields: snowflakeSkillSubFields,
                },
                {
                  name: 'target_geography',
                  label: 'target_geography',
                  value: '',
                  required: true,
                  fieldType: 'string',
                  parameterId: 'target-geography',
                  subFields: snowflakeTargetGeoSubFields,
                },
                {
                  name: 'comparison_geography',
                  label: 'comparison_geography',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'comparison-geography',
                  subFields: snowflakeComparisonGeoSubFields,
                },
                {
                  name: 'seniority_levels',
                  label: 'seniority_levels',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                },
              ],
            },
          },
        },
        {
          id: 'universities-sankey',
          title:
            'Top universities{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}} Roles{{/if}} in {{PARAM "Target Geography"}}',
          position: { y: 14, height: 16 },
          plot: {
            chartType: 'sankey',
            format: FormatType.INTEGER,
            plotFilters: [{ type: PlotFilterType.TOP_N, value: 10 }],
            plotParameters: [
              {
                parameterId: '50fab991-e99e-4839-90b7-9e0e29b34a64',
                grouped: false,
              },
            ],
            defaultMetricOptions: ['transitions_inflow'],
            dataSource: 'snowflake',
            snowflakeConfig: {
              savedQueryId: 'university-geo-transitions',
              fields: [
                {
                  name: 'role',
                  label: 'role',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  subFields: snowflakeRoleSubFields,
                  parameterId: 'occupation',
                },
                {
                  name: 'skill',
                  label: 'skill',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  subFields: snowflakeSkillSubFields,
                },
                {
                  name: 'geography',
                  label: 'geography',
                  value: '',
                  required: true,
                  fieldType: 'string',
                  subFields: [
                    {
                      name: 'geo_level',
                      fieldType: 'string' as const,
                      selectionLevelIds: [] as string[],
                      extractMode: 'levelName' as const,
                    },
                    {
                      name: 'geo_id',
                      fieldType: 'number' as const,
                      selectionLevelIds: [] as string[],
                    },
                  ],
                  parameterId: 'target-geography',
                },
                {
                  name: 'seniority_levels',
                  label: 'seniority_levels',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                },
              ],
            },
          },
          isTitleManuallySet: false,
        },
      ],
      [
        {
          id: 'competitive-landscape-text',
          title: '',
          position: { height: 8 },
          plot: {
            chartType: 'text',
            textHtml:
              '## Competitive landscape\n\nThe competitive dynamics among companies in this landscape tread a fine line. An abundance of firms suggests a wealth of talent and qualifications, yet it can escalate competition and increase hiring costs. On the flip side, when concentration is high, with only a handful of companies dominating the market, it can signal potential barriers to entry for new players and pose significant challenges for them to establish themselves in the market.',
          },
        },
        {
          id: 'competitive-landscape-table',
          title: null,
          position: { y: 8, height: 16 },
          plot: {
            chartType: 'table',
            dimensionSource: 'company',
            defaultMetricOptions: competitiveLandscapeMetrics,
            tableColumns: [
              {
                id: 'competitive-landscape-table-geography',
                kind: 'dimension',
                dimension: 'geography',
                alignment: 'left',
              },
              ...competitiveLandscapeMetrics.map(
                (metric): TableColumnConfig => ({
                  id: `competitive-landscape-table-${metric}`,
                  kind: 'variable',
                  metric,
                  aggregation: 'sum',
                })
              ),
            ],
            plotParameters: [targetGeoParam, occupationParam],
            snowflakeConfig: {
              savedQueryId: 'competitive-landscape-sentiment-prestige',
              fields: snowflakeLandscapeFields,
            },
          },
        },
        {
          id: 'market-concentration-dynamic-text',
          title: '',
          position: { y: 24, width: 25, height: 10 },
          plot: {
            chartType: 'text',
            dimensionSource: 'company',
            plotParameters: [targetGeoParam, occupationParam],
            defaultMetricOptions: ['market_share'],
            snowflakeConfig: {
              savedQueryId: 'market-concentration-rank',
              fields: [
                {
                  name: 'role',
                  label: 'Role',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'occupation',
                  subFields: snowflakeRoleSubFields,
                },
                {
                  name: 'skill',
                  label: 'Skill',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'skills',
                  subFields: snowflakeSkillSubFields,
                },
                {
                  name: 'target_geography',
                  label: 'Target Geography',
                  value: '',
                  required: true,
                  fieldType: 'string',
                  parameterId: 'target-geography',
                  subFields: snowflakeTargetGeoSubFields,
                },
                {
                  name: 'seniority_levels',
                  label: 'Seniority',
                  value: '',
                  required: false,
                  fieldType: 'number[]',
                  parameterId: 'seniority',
                },
              ],
            },
            textHtml: `{{#if (HAS_DATA "market_share")}}
{{#if (lte (VALUE "sq:market-concentration-rank") 0.2)}}
The hiring market is highly competitive across many firms with no dominant players. {{ARG_MAX "market_share"}} emerges as the top employer, engaging {{FORMAT (VALUE "market_share" entity=(ARG_MAX "market_share")) format="%"}} of workers in {{PARAM "Occupation"}} in {{PARAM "Target Geography"}}, while the top companies employ only {{FORMAT (SUM "market_share") format="%"}} of the workforce.
{{else if (lte (VALUE "sq:market-concentration-rank") 0.4)}}
The hiring market is fairly balanced among several firms, with no single dominant player. Although {{ARG_MAX "market_share"}} holds the largest market share at {{FORMAT (VALUE "market_share" entity=(ARG_MAX "market_share")) format="%"}}, the top companies collectively employ only {{FORMAT (SUM "market_share") format="%"}} of workers in {{PARAM "Occupation"}} in {{PARAM "Target Geography"}}.
{{else if (lte (VALUE "sq:market-concentration-rank") 0.6)}}
The hiring market exhibits moderate concentration, with a few firms holding notable shares but no overwhelming dominance. {{ARG_MAX "market_share"}} leads the market with {{FORMAT (VALUE "market_share" entity=(ARG_MAX "market_share")) format="%"}} of workers in {{PARAM "Occupation"}} in {{PARAM "Target Geography"}}, yet the top companies account for just {{FORMAT (SUM "market_share") format="%"}} of the workforce.
{{else if (lte (VALUE "sq:market-concentration-rank") 0.8)}}
The hiring market shows considerable concentration, with several dominant firms significantly impacting the landscape. {{ARG_MAX "market_share"}} stands out as the top employer, employing {{FORMAT (VALUE "market_share" entity=(ARG_MAX "market_share")) format="%"}} of workers in {{PARAM "Occupation"}} in {{PARAM "Target Geography"}}, and the top companies collectively hire {{FORMAT (SUM "market_share") format="%"}} of the workforce.
{{else}}
The hiring market is highly concentrated, with a few firms exerting significant control over employment opportunities. {{ARG_MAX "market_share"}} dominates the market, employing {{FORMAT (VALUE "market_share" entity=(ARG_MAX "market_share")) format="%"}} of workers in {{PARAM "Occupation"}} in {{PARAM "Target Geography"}}, while the top companies command {{FORMAT (SUM "market_share") format="%"}} of the workforce.
{{/if}}
{{else}}
There's currently no available data to assess the market concentration in {{PARAM "Target Geography"}}.
{{/if}}`,
          },
        },
      ],
      [
        {
          id: 'recommendations-scores-tightness',
          title: '',
          position: { height: 20 },
          plot: {
            chartType: 'text',
            dataSource: 'snowflake',
            dimensionSource: 'company',
            defaultMetricOptions: ['market_share'],
            plotParameters: [
              targetGeoParam,
              comparisonGeoGrouped,
              occupationParam,
            ],
            snowflakeConfig: {
              savedQueryId: 'market-concentration-rank',
              fields: snowflakeLandscapeFields,
            },
            textHtml: `## Recommendations\n\n
{{#if (HAS_DATA "demand_supply_percentile")}}
* The ratio of employees to job postings in {{PARAM "Target Geography"}} indicates {{#if (lt (VALUE "demand_supply_percentile") 0.4)}}a favorable level of competition{{#if (HAS_PARAM "Occupation")}} for the {{PARAM "Occupation"}} role{{/if}}{{else}}{{#if (gte (VALUE "demand_supply_percentile") 0.6)}}significant competition{{#if (HAS_PARAM "Occupation")}} for the {{PARAM "Occupation"}} role{{/if}}{{else}}a balanced level of competition{{#if (HAS_PARAM "Occupation")}} for the {{PARAM "Occupation"}} role{{/if}}{{/if}}{{/if}}, {{#if (lt (VALUE "wage_pressure_percentile") 0.4)}}with a low level of salary growth{{else}}{{#if (gte (VALUE "wage_pressure_percentile") 0.6)}}with salary growth indicating significant wage pressure compared to the comparison geography{{else}}with salary growth reflecting a moderate level compared to the comparison geography{{/if}}{{/if}}, {{#if (lt (VALUE "time_to_fill_percentile") 0.4)}}and vacancies being filled quickly.{{else}}{{#if (gte (VALUE "time_to_fill_percentile") 0.6)}}and vacancies taking a considerable amount of time to fill.{{else}}and an average time-to-fill for vacancies.{{/if}}{{/if}}
{{/if}}
{{#if (HAS_DATA "market_tightness")}}
{{#if (lte (VALUE "market_tightness") 0.2)}}* Overall, the employment market in {{PARAM "Target Geography"}} overwhelmingly favors employers. This allows companies to exercise a high degree of selectivity in their hiring decisions.
{{else if (lte (VALUE "market_tightness") 0.4)}}* Overall, the job market leans moderately towards employers. This situation grants employers an advantage and allows for some degree of selectivity in hiring decisions.
{{else if (lte (VALUE "market_tightness") 0.6)}}* Overall, there is a balanced job market. Neither employers nor employees hold a significant advantage. As an employer, offering the prevailing market rate should suffice to attract the desired talent.
{{else if (lte (VALUE "market_tightness") 0.8)}}* Overall, there is a moderately tight job market. Employers encounter some competition for qualified candidates, suggesting that offering slightly above the prevailing market rate may be necessary to attract desired talent.
{{else}}* Overall, there is an extremely tight job market. Employers contend with intense competition for qualified candidates, necessitating offering significantly above the prevailing market rate to attract the desired talent.
{{/if}}
{{/if}}
{{#if (HAS_DATA "headcount" secondaryDimension="gender")}}
{{#if (HAS_DATA "headcount" secondaryDimension="ethnicity")}}
* Demographically, {{PARAM "Target Geography"}} shows a {{#if (gt (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) 10)}}high{{else if (gt (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) 0)}}similar{{else if (gt (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) -10)}}low{{else}}very low{{/if}} female representation {{#if (gt (MATH "(tg - cg) * 100" tg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Target Geography") cg=(SHARE "headcount" secondaryDimension="gender" segment="Female" entity="Comparison Geography")) 0)}}{{#if (gt (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 0)}}and{{else}}but{{/if}}{{else}}{{#if (lte (MATH "(cw - tw) * 100" cw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Comparison Geography") tw=(SHARE "headcount" secondaryDimension="ethnicity" segment="White" entity="Target Geography")) 0)}}and{{else}}but{{/if}}{{/if}} ethnic diversity compared to the comparison geography.{{#if (HAS_DATA "tenure")}} Additionally, the average relevant experience of workers{{#if (HAS_PARAM "Occupation")}} in the {{PARAM "Occupation"}} role{{/if}} in {{PARAM "Target Geography"}} is slightly {{#if (gte (subtract (VALUE "tenure" entity=(PARAM "Target Geography")) (VALUE "tenure" entity="Comparison Geography")) 0)}}higher{{else}}lower{{/if}} than in the comparison geography.{{/if}}
{{/if}}
{{/if}}
{{#if (HAS_DATA "market_share")}}
{{#if (lte (VALUE "sq:market-concentration-rank") 0.2)}}* The top ten companies in {{PARAM "Target Geography"}} collectively employ {{FORMAT (SUM "market_share") format="%"}} of the workforce. This underscores the competitive nature of the job market, where no single company dominates the landscape.
{{else if (lte (VALUE "sq:market-concentration-rank") 0.4)}}* The top ten companies in {{PARAM "Target Geography"}} collectively employ {{FORMAT (SUM "market_share") format="%"}} of the workforce. This indicates a fairly balanced job market with several firms vying for talent, and no single entity holding significant dominance.
{{else if (lte (VALUE "sq:market-concentration-rank") 0.6)}}* The top ten companies in {{PARAM "Target Geography"}} collectively employ {{FORMAT (SUM "market_share") format="%"}} of the workforce. This suggests a moderately concentrated job market where a few firms hold notable shares, but no single entity exerts overwhelming control.
{{else if (lte (VALUE "sq:market-concentration-rank") 0.8)}}* The top ten companies in {{PARAM "Target Geography"}} collectively employ {{FORMAT (SUM "market_share") format="%"}} of the workforce. This indicates a job market with considerable concentration, where several dominant firms significantly impact employment opportunities.
{{else}}* The top ten companies in {{PARAM "Target Geography"}} collectively employ {{FORMAT (SUM "market_share") format="%"}} of the workforce. This highlights a highly concentrated job market, with a few firms exerting significant control over employment opportunities.
{{/if}}
{{/if}}
{{#if (HAS_DATA "target_companies_rec")}}
{{#if (eq (COUNT "target_companies_rec") 1)}}* {{LIST "target_companies_rec" limit="1"}} stands out as a potential target, offering the opportunity to hire high-prestige employees at a lower cost due to its combination of low salaries and low sentiment scores.
{{else}}* {{LIST "target_companies_rec" limit="3"}} are highlighted as potential targets that offer the opportunity to hire high-prestige employees at a lower cost due to their combination of low salaries and low sentiment scores.
{{/if}}
{{/if}}`,
          },
        },
      ],
    ],
    1
  );

  return {
    id: 'report-company-market-conditions',
    name: 'Market Conditions',
    type: PageType.REPORT,
    owner: {
      id: 'revelio-labs',
      username: 'revelio-labs',
    },
    sampleFilterGroups: encodeFilterGroups(
      buildFilterGroups(
        [
          {
            groupKey: 'target-geography',
            filterIds: [FilterId.GEOGRAPHY],
            defaultSelections: {
              [FilterId.GEOGRAPHY]: [
                { id: '1_1_2', name: 'New York City', levelId: 'msa' },
              ],
            },
          },
          {
            groupKey: 'comparison-geography',
            filterIds: [FilterId.GEOGRAPHY],
            defaultSelections: {
              [FilterId.GEOGRAPHY]: [
                { id: '1_1_11', name: 'Chicago', levelId: 'msa' },
                { id: '1_1_21', name: 'San Francisco', levelId: 'msa' },
              ],
            },
          },
          {
            groupKey: 'occupation',
            filterIds: [FilterId.ROLE, FilterId.ROLE_TAXONOMY],
            defaultSelections: {
              [FilterId.ROLE]: [
                {
                  id: '7_134',
                  name: 'Software Engineer',
                  levelId: 'role_k150',
                },
              ],
            },
          },
        ],
        {
          primaryFilterId: FilterId.GEOGRAPHY,
          taxonomyConfig: {
            roleTaxonomyVersion: GENERATED_ROLE_DEFAULTS_VERSION,
          },
        }
      )
    ),
    tabs: [
      {
        label: 'Key factors to compare markets',
        id: 'default',
        isActive: true,
        tabContent: {
          id: 'market-conditions-content',
          grid,
          title: 'Market Conditions',
          description:
            'Understanding market conditions and its implications for talent acquisition and retention is essential for navigating the competitive labor landscape and ensuring organizational resilience.',
          items,
          primaryFilter: {
            id: FilterId.GEOGRAPHY,
          },
          secondaryFilters: [
            { id: FilterId.GEOGRAPHY },
            { id: FilterId.ROLE },
            { id: FilterId.CUSTOM_SKILL },
            { id: FilterId.SENIORITY },
          ],
          reportParameters: [
            {
              parameterId: 'target-geography',
              filterId: FilterId.GEOGRAPHY,
              isPrimary: true,
              name: 'Target Geography',
            },
            {
              parameterId: 'comparison-geography',
              filterId: FilterId.GEOGRAPHY,
              isPrimary: false,
              name: 'Comparison Geography',
              selectionLimit: 5,
              allowMultiLevelSelect: true,
            },
            {
              parameterId: 'occupation',
              filterId: FilterId.ROLE,
              isPrimary: false,
              name: 'Occupation',
              selectionLimit: 10,
            },
            {
              parameterId: 'skills',
              filterId: FilterId.CUSTOM_SKILL,
              isPrimary: false,
              name: 'Skills',
              isOptional: true,
              selectionLimit: 7,
            },
            {
              parameterId: 'seniority',
              filterId: FilterId.SENIORITY,
              isPrimary: false,
              name: 'Seniority',
              isOptional: true,
              selectionLimit: 7,
            },
          ],
        },
      },
    ],
  };
})();
